"use client";

import type React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDictionary } from "@/core/hooks/use-dictionary";
import TextInput from "@/components/account/input/TextInput";
import { Loader2 } from "lucide-react";
import { Heading } from "@/components/ui/Heading";
import FileUploadInput from "@/components/account/input/MultipleFileUploadInput";
import { useState } from "react";
import { ImageTs } from "@/core/types/imageTs";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton")
import { createArtworks } from "@/core/lib/api/main/submission";
import { toast } from "sonner";
import { getSubmitArtworkSchema, SubmitArtworkFormData, } from "@/core/schemas/submissionArtworkSchema";
import Container from "@/components/shared/Container";
import TextareaInput from "@/components/account/input/TextareaInput";
import CaptchaField from "@/components/main/login/CaptchaField";

type ExtendedSubmitArtworkFormData = SubmitArtworkFormData & {
    attachment_ids: number[];
};

export default function SubmitArtwork() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [currentImageIds, setCurrentImageIds] = useState<number[]>([]);
    const [currentDefaultImages, setCurrentDefaultImages] = useState<ImageTs[]>([]);
    const [uploadKey, setUploadKey] = useState(0);

    const { dictionary } = useDictionary();

    const submitArtworkSchema = getSubmitArtworkSchema(dictionary);

    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = useForm<SubmitArtworkFormData>({
        resolver: zodResolver(submitArtworkSchema),
        defaultValues: {
            recaptcha_token: "",
        },
    });

    const showImageError = () => {
        setError("root.images", {
            type: "custom",
            message: dictionary.submission.pleaseUploadAtLeastOneImage,
        });
    };

    const onSubmit = async (data: SubmitArtworkFormData) => {
        if (currentImageIds.length === 0) {
            showImageError();
            return;
        } else {
            clearErrors("root.images");
        }

        try {
            setIsSubmitting(true);

            const payload: ExtendedSubmitArtworkFormData = {
                ...(Object.fromEntries(
                    Object.entries(data).map(([key, value]) => [key, value ?? ""])
                ) as SubmitArtworkFormData),
                attachment_ids: currentImageIds,
            };

            const result = await createArtworks(payload);
            toast.success(result.message);

            reset();
            setCurrentImageIds([]);
            setCurrentDefaultImages([]);
            setUploadKey((prev) => prev + 1);
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const removeImageById = (id: number) => {
        setCurrentImageIds((prev) => prev.filter((imgId) => imgId !== id));
        setCurrentDefaultImages((prev) => prev.filter((img) => img.id !== id));
    };

    const onCaptchaChange = (token: string | null) => {
        setValue("recaptcha_token", token || "");
    };

    return (
        <Container>
            <div>
                <Heading level={1} className="text-center my-11">{dictionary.submission.artGallery}</Heading>
                <p className="text-primary-600 mb-11 text-sm md:text-base text-justify">{dictionary.submission.artworkDescription}</p>
                <div className="flex gap-3 items-center mb-11">
                    <p className="text-primary-600 text-nowrap text-sm md:text-base">{dictionary.submission.joinOpening}</p>
                    <div className="border-t border-border w-full h-0.5" />
                </div>
            </div>
            <div className="bg-gray-50 rounded-xs py-7 px-8">
                <form
                    onSubmit={handleSubmit(onSubmit, () => {
                        if (currentImageIds.length === 0) showImageError();
                    })}
                    className="w-full">
                    <Heading level={3} className="mb-8">{dictionary.submission.artistInfo}</Heading>

                    <div className="grid grid-cols-6 gap-4 w-full mb-6">
                        <TextInput
                            label={dictionary.forms.firstName}
                            name="artist_name"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.firstname}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />
                        <TextInput
                            label={dictionary.forms.lastName}
                            name="artist_family"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.lastname}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />

                        <TextInput
                            label={dictionary.ui.profile.phone}
                            name="artist_phone"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.ui.profile.phone}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />

                        <TextInput
                            label={dictionary.ui.socialMedia.instagram}
                            name="artist_instagram"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.ui.socialMedia.instagram}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />

                        <TextInput
                            label={dictionary.forms.email}
                            name="artist_email"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.email}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-full md:col-span-3"
                        />

                        <Heading level={3} className="col-span-full mb-8 mt-14">
                            {dictionary.submission.artworkInfo}
                        </Heading>

                        <TextInput
                            label={dictionary.forms.artworkName}
                            name="title"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.artworkName}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />

                        <TextInput
                            label={dictionary.forms.dimensions}
                            name="dimensions"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.dimensions}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />

                        <TextInput
                            label={dictionary.forms.technique}
                            name="technique"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.technique}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />

                        <TextInput
                            label={dictionary.forms.price}
                            name="price"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.price}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-3"
                        />

                        <TextareaInput
                            label={dictionary.forms.description}
                            name="description"
                            register={register}
                            errors={errors}
                            placeholder={dictionary.forms.description}
                            disabled={isSubmitting}
                            inputClassName="rounded-xs"
                            className="col-span-full"
                        />

                        <Heading level={3} className="col-span-full mb-8 mt-14">
                            {dictionary.submission.submitArtwork}
                        </Heading>

                        <div className="col-span-full mb-5 min-h-36">
                            <FileUploadInput
                                key={uploadKey}
                                multiple
                                onUploadSuccess={(images) => {
                                    setIsUploading(false); // upload finished
                                    if (Array.isArray(images)) {
                                        clearErrors("root.images");
                                        setCurrentImageIds((prev) => [
                                            ...prev,
                                            ...images.map((img) => img.id),
                                        ]);
                                        setCurrentDefaultImages((prev) => [...prev, ...images]);
                                    }
                                }}
                                onFilesChange={(files) => {
                                    // start upload when files selected
                                    if (files && files.length > 0) {
                                        setIsUploading(true);
                                    }
                                }}
                                defaultImages={currentDefaultImages}
                                onDefaultRemove={removeImageById}
                            />
                            {errors?.root?.images && (
                                <p className="text-destructive text-sm mt-2">
                                    {errors.root.images.message}
                                </p>
                            )}
                        </div>

                        <div className="justify-center grid col-span-full min-h-28">
                            <CaptchaField
                                key={uploadKey}
                                error={errors.recaptcha_token?.message}
                                onChange={onCaptchaChange}
                            />
                        </div>
                    </div>

                    <CustomButton
                        type="submit"
                        disabled={isSubmitting || isUploading}
                        variant="black"
                        size="full"
                    >
                        {isSubmitting ? (
                            <Loader2 className="animate-spin h-5 w-5 mr-2" />
                        ) : (
                            dictionary.common.submit
                        )}
                    </CustomButton>
                </form>
            </div>
        </Container>
    );
}